class ShopifyDiscountManager {
  constructor(config) {
    this.config = {
      validDiscountCodes: [],
      // Default fallback (will be overridden by implementation)
      discountTagPrefix: 'winterdiscount-', 
      dataAttributeHostSelector: '[data-product-id]', 

      priceSelectors: {
        priceContainer: '.price',
        currentPrice: 'span.theme-money:not(.was-price)',
        wasPrice: 'span.was-price',
        productPage: '.price-area.product-detail__gap-sm'
      },
      cookieName: 'discount_code',
      styles: `
        .price-updating {
          opacity: 0;
          transition: opacity 0.1s;
        }
        .price-updated {
          opacity: 1;
        }
      `,
      currencyFormats: {
        USD: {
          decimals: 2,
          decimalSeparator: '.',
          thousandsSeparator: ',',
          symbol: '$'
        },
        EUR: {
          decimals: 2,
          decimalSeparator: ',',
          thousandsSeparator: '.',
          symbol: '€'
        },
        PLN: {
          decimals: 2,
          decimalSeparator: ',',
          thousandsSeparator: ' ',
          symbol: 'zł'
        },
        GBP: {
          decimals: 2,
          decimalSeparator: '.',
          thousandsSeparator: ',',
          symbol: '£'
        },
        CHF: {
          decimals: 2,
          decimalSeparator: '.',
          thousandsSeparator: "'", 
          symbol: 'CHF'
        }
      },
      giftCards: {
        productIds: [],
        variantIds: []
      },
      ...config
    };
    
    this.originalPrices = new Map();
    this.observer = null;
    this.initialized = false;
    this.styleElement = null;
    
    this.updateAllPrices = this.updateAllPrices.bind(this);
    this.handleMutations = this.handleMutations.bind(this);
  }

  init() {
    if (this.initialized) {
      console.warn('Discount manager already initialized');
      return;
    }
    
    const discountCode = this.getDiscountCookie();
    if (!discountCode || !this.isValidDiscountCode(discountCode)) {
      console.debug('No valid discount code found, manager will not run.');
      return;
    }

    try {
      this.injectStyles();
      this.setupPriceObserver();
      this.updateAllPrices();
      this.initialized = true;
      console.debug('Discount manager initialized successfully');
    } catch (error) {
      console.error('Failed to initialize discount manager:', error);
    }
  }

  injectStyles() {
    if (!this.styleElement) {
      this.styleElement = document.createElement('style');
      this.styleElement.textContent = this.config.styles;
      document.head.appendChild(this.styleElement);
    }
  }

  isValidDiscountCode(code) {
    return this.config.validDiscountCodes.includes(code);
  }

  getDiscountCookie() {
    try {
      return document.cookie
        .split('; ')
        .find(row => row.startsWith(`${this.config.cookieName}=`))
        ?.split('=')[1];
    } catch (error) {
      console.error('Error reading discount cookie:', error);
      return null;
    }
  }

  detectCurrencyFormat(priceString) {
    if (!priceString || typeof priceString !== 'string') return this.config.currencyFormats.USD;
    const cleaned = priceString.trim();
    
    if (cleaned.includes('CHF')) return this.config.currencyFormats.CHF;
    if (cleaned.includes('£') || cleaned.startsWith('GBP')) return this.config.currencyFormats.GBP;
    if (cleaned.includes('€') || cleaned.startsWith('EUR')) return this.config.currencyFormats.EUR;
    if (cleaned.includes('zł') || cleaned.startsWith('PLN')) return this.config.currencyFormats.PLN;
    
    return this.config.currencyFormats.USD;
  }

  parsePriceString(priceString) {
    if (!priceString || typeof priceString !== 'string') {
      console.warn('Invalid price string:', priceString);
      return 0;
    }
    try {
      const format = this.detectCurrencyFormat(priceString);
      let cleaned = priceString.replace(/[^\d.,]/g, '');
      if (format.decimalSeparator === ',') {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
      return parseFloat(cleaned);
    } catch (error) {
      console.error('Error parsing price:', error);
      return 0;
    }
  }

  calculateDiscount(price, priceContainerElement) {
    if (typeof price !== 'number' || isNaN(price) || !priceContainerElement) {
      return price;
    }

    const productHost = priceContainerElement.closest(this.config.dataAttributeHostSelector);
    if (!productHost) return price;

    const discountTag = productHost.dataset.discountTag;
    if (!discountTag) return price;
    
    const activeCode = this.getDiscountCookie();

    // STRICT CHECK: Only run if the code is "wintersale25"
    if (activeCode !== 'wintersale25') {
      return price;
    }

    // Check if tag matches config (e.g., "winterdiscount-")
    if (discountTag.startsWith(this.config.discountTagPrefix)) {
      // Extract number: "winterdiscount-60" -> "60"
      const numberPart = discountTag.replace(this.config.discountTagPrefix, '');
      const discountPercentage = parseInt(numberPart, 10);
      
      if (!isNaN(discountPercentage) && discountPercentage > 0 && discountPercentage < 100) {
        return price * (1 - (discountPercentage / 100));
      }
    }

    return price;
  }

  formatPrice(price, template) {
    if (!template || typeof price !== 'number' || isNaN(price)) {
      return template || '0.00';
    }
    try {
      const format = this.detectCurrencyFormat(template);
      let formattedPrice = price.toFixed(format.decimals);
      if (format.decimalSeparator === ',') {
        formattedPrice = formattedPrice.replace('.', ',');
      }
      const parts = formattedPrice.split(format.decimalSeparator);
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator);
      formattedPrice = parts.join(format.decimalSeparator);
      const trimmedTemplate = template.trim();
      
      if (format.symbol === 'CHF') {
         return `CHF ${formattedPrice}`;
      }

      const symbolIsPrefix = /^[^\d]/.test(trimmedTemplate);
      if (symbolIsPrefix) {
        return `${trimmedTemplate.charAt(0)}${formattedPrice}`;
      } else {
        return `${formattedPrice} ${trimmedTemplate.slice(-2)}`;
      }
    } catch (error) {
      console.error('Error formatting price:', error);
      return template;
    }
  }

  generatePriceId() {
    return `price_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  isGiftCard(element) {
    if (!element) return false;
    try {
      const productContainer = element.closest('[data-product-id]');
      if (!productContainer) return false;
      const productId = productContainer.dataset.productId;
      const variantId = productContainer.querySelector('[data-variant-id]')?.dataset.variantId;
      return (
        this.config.giftCards.productIds.includes(productId) ||
        (variantId && this.config.giftCards.variantIds.includes(variantId))
      );
    } catch (error) {
      return false;
    }
  }

  isProductPage(element) {
    return !!element.closest('.product-area');
  }

  updatePriceElement(priceContainer) {
    const discountCode = this.getDiscountCookie();
    if (!discountCode || !this.isValidDiscountCode(discountCode)) {
      return; 
    }
    if (!priceContainer || !(priceContainer instanceof Element)) {
      return;
    }
    if (this.isGiftCard(priceContainer)) {
      return;
    }
    
    try {
      priceContainer.classList.add('price-updating');
      const priceId = priceContainer.dataset.priceId || this.generatePriceId();
      priceContainer.dataset.priceId = priceId;

      if (!this.originalPrices.has(priceId)) {
        const currentPriceEl = priceContainer.querySelector(this.config.priceSelectors.currentPrice);
        if (!currentPriceEl) return;
        this.originalPrices.set(priceId, {
          container: priceContainer,
          originalHTML: priceContainer.innerHTML,
          currentPrice: currentPriceEl.textContent,
        });
      }

      const original = this.originalPrices.get(priceId);
      const basePrice = this.parsePriceString(original.currentPrice);
      const discountedPrice = this.calculateDiscount(basePrice, priceContainer);
      
      if (discountedPrice >= basePrice) {
        priceContainer.classList.remove('price-updating');
        return;
      }

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = original.originalHTML;
      const existingWasPriceElement = tempDiv.querySelector(this.config.priceSelectors.wasPrice);
      
      let wasPriceHTML;
      if (existingWasPriceElement) {
        wasPriceHTML = existingWasPriceElement.outerHTML;
      } else {
        wasPriceHTML = `<span class="was-price theme-money">${original.currentPrice}</span>`;
      }

      const isProductPage = this.isProductPage(priceContainer);
      const fontSizeStyle = isProductPage ? ' style="font-size: 1.25em;"' : '';

      priceContainer.innerHTML = `
        <span class="theme-money"${fontSizeStyle}>${this.formatPrice(discountedPrice, original.currentPrice)}</span>
        ${wasPriceHTML}
      `;

      requestAnimationFrame(() => {
        priceContainer.classList.remove('price-updating');
        priceContainer.classList.add('price-updated');
      });
    } catch (error) {
      console.error('Error updating price element:', error);
      const original = this.originalPrices.get(priceContainer.dataset.priceId);
      if (original) priceContainer.innerHTML = original.originalHTML;
      priceContainer.classList.remove('price-updating');
    }
  }

  updateAllPrices() {
    const containers = document.querySelectorAll(this.config.priceSelectors.priceContainer);
    containers.forEach(container => this.updatePriceElement(container));
  }

  handleMutations(mutations) {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches(this.config.priceSelectors.priceContainer) || node.querySelector(this.config.priceSelectors.priceContainer)) {
              this.updateAllPrices();
            }
          }
        });
      }
    });
  }

  setupPriceObserver() {
    if (this.observer) this.observer.disconnect();
    this.observer = new MutationObserver(this.handleMutations);
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  destroy() {
    try {
      if (this.observer) this.observer.disconnect();
      if (this.styleElement) {
        this.styleElement.remove();
        this.styleElement = null;
      }
      this.originalPrices.forEach(({container, originalHTML}) => {
        if (container && container instanceof Element) {
          container.innerHTML = originalHTML;
          container.classList.remove('price-updating', 'price-updated');
        }
      });
      this.originalPrices.clear();
      this.initialized = false;
    } catch (error) {
      console.error('Error destroying discount manager:', error);
    }
  }
}